import { useMemo, useState } from 'react';
import { Info, RotateCcw } from 'lucide-react';
import type { VideoPart, VideoStyle, VideoStyleProp, VideoStyleTheme } from '../../types/quizzes';
import { STYLE_PROPS, VIDEO_FONT_OPTIONS, VIDEO_PARTS, defaultVideoStyle } from '../../lib/videoQuiz';
import VideoPlayerPreview from './VideoPlayerPreview';

const HEX = /^#[0-9a-fA-F]{6}$/;

function ColorField({ value, onChange, fallback = '#000000' }: { value: string; onChange: (v: string) => void; fallback?: string }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={HEX.test(value) ? value : fallback} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 rounded border border-line cursor-pointer flex-shrink-0" />
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="ds-input w-full min-w-0" />
    </div>
  );
}

const THEME_FIELDS: Array<{ key: keyof VideoStyleTheme; label: string; kind: 'color' | 'text' | 'font'; placeholder?: string }> = [
  { key: 'fontFamily', label: 'Font', kind: 'font' },
  { key: 'primaryColor', label: 'Primary color', kind: 'color' },
  { key: 'textColor', label: 'Text color', kind: 'color' },
  { key: 'backgroundColor', label: 'Background', kind: 'color' },
  { key: 'cardBackground', label: 'Card background', kind: 'color' },
  { key: 'borderRadius', label: 'Corner radius', kind: 'text', placeholder: '12px' },
  { key: 'spacing', label: 'Spacing', kind: 'text', placeholder: '16px' },
  { key: 'maxWidth', label: 'Max width', kind: 'text', placeholder: '960px' },
];

export default function VideoStyleTab({
  quizId, style: rawStyle, onChange, previewKey,
}: {
  quizId: string;
  style: VideoStyle | undefined;
  onChange: (style: VideoStyle) => void;
  previewKey: number;
}) {
  const style: VideoStyle = useMemo(() => {
    const defaults = defaultVideoStyle();
    return {
      theme: { ...defaults.theme, ...(rawStyle?.theme ?? {}) },
      components: rawStyle?.components ?? {},
      customCss: rawStyle?.customCss ?? '',
    };
  }, [rawStyle]);
  const [part, setPart] = useState<VideoPart>('question-card');

  const setTheme = (key: keyof VideoStyleTheme, value: string) => onChange({ ...style, theme: { ...style.theme, [key]: value } });

  const partProps = style.components[part] ?? {};
  const setPartProp = (prop: VideoStyleProp, value: string) => {
    const nextPart = { ...partProps };
    if (value === '') delete nextPart[prop];
    else nextPart[prop] = value;
    const components = { ...style.components };
    if (Object.keys(nextPart).length === 0) delete components[part];
    else components[part] = nextPart;
    onChange({ ...style, components });
  };
  const resetPart = () => {
    const components = { ...style.components };
    delete components[part];
    onChange({ ...style, components });
  };

  const groups = Array.from(new Set(VIDEO_PARTS.map((p) => p.group)));
  const cssBytes = new Blob([style.customCss ?? '']).size;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div className="space-y-6 min-w-0">
        {/* Theme */}
        <section>
          <h3 className="text-sm font-semibold text-ink mb-3">Theme</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {THEME_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="ds-label">{f.label}</label>
                {f.kind === 'color' ? (
                  <ColorField value={style.theme[f.key]} onChange={(v) => setTheme(f.key, v)} />
                ) : f.kind === 'font' ? (
                  <select value={style.theme.fontFamily} onChange={(e) => setTheme('fontFamily', e.target.value)} className="ds-input w-full">
                    {!VIDEO_FONT_OPTIONS.includes(style.theme.fontFamily) && <option value={style.theme.fontFamily}>{style.theme.fontFamily}</option>}
                    {VIDEO_FONT_OPTIONS.map((ft) => <option key={ft} value={ft}>{ft}</option>)}
                  </select>
                ) : (
                  <input type="text" value={style.theme[f.key]} placeholder={f.placeholder} onChange={(e) => setTheme(f.key, e.target.value)} className="ds-input w-full" />
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Per component */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-ink">Individual parts</h3>
            {Object.keys(partProps).length > 0 && (
              <button type="button" onClick={resetPart} className="flex items-center gap-1 text-xs text-ink-3 hover:text-ink"><RotateCcw className="w-3.5 h-3.5" /> Reset this part</button>
            )}
          </div>
          <label className="ds-label">Part</label>
          <select value={part} onChange={(e) => setPart(e.target.value as VideoPart)} className="ds-input w-full sm:w-72 mb-4">
            {groups.map((g) => (
              <optgroup key={g} label={g}>
                {VIDEO_PARTS.filter((p) => p.group === g).map((p) => (
                  <option key={p.value} value={p.value}>{p.label}{style.components[p.value] ? ' •' : ''}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {STYLE_PROPS.map((p) => {
              const v = partProps[p.value] ?? '';
              return (
                <div key={p.value}>
                  <label className="text-xs text-ink-3 mb-1 block">{p.label}</label>
                  {p.kind === 'color' ? (
                    <div className="flex items-center gap-2">
                      <input type="color" value={HEX.test(v) ? v : '#000000'} onChange={(e) => setPartProp(p.value, e.target.value)} className="h-9 w-12 rounded border border-line cursor-pointer flex-shrink-0" />
                      <input type="text" value={v} placeholder="inherit" onChange={(e) => setPartProp(p.value, e.target.value)} className="ds-input w-full min-w-0" />
                    </div>
                  ) : p.kind === 'select' ? (
                    <select value={v} onChange={(e) => setPartProp(p.value, e.target.value)} className="ds-input w-full">
                      <option value="">inherit</option>
                      {p.options?.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type="text" value={v} placeholder={p.placeholder} onChange={(e) => setPartProp(p.value, e.target.value)} className="ds-input w-full" />
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Custom CSS */}
        <section>
          <h3 className="text-sm font-semibold text-ink mb-2">Custom CSS</h3>
          <div className="text-xs text-ink-3 mb-2 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Scoped into the quiz's Shadow DOM, so it only affects the quiz. Every element has a part name
              (the same names as the list above): select them with
              {' '}<code className="font-mono">[part~="answer-button"]:hover {'{ … }'}</code>.
              From your page's own CSS, use <code className="font-mono">mls-video-quiz::part(answer-button)</code>.
              {' '}<code className="font-mono">@import</code>, scripts and external <code className="font-mono">url()</code> are rejected.
            </span>
          </div>
          <textarea
            value={style.customCss ?? ''}
            onChange={(e) => onChange({ ...style, customCss: e.target.value })}
            rows={8}
            spellCheck={false}
            placeholder={'[part~="question-title"] {\n  letter-spacing: -0.01em;\n}'}
            className="ds-input w-full font-mono text-xs resize-y"
          />
          <p className={`text-xs mt-1 ${cssBytes > 20480 ? 'text-danger' : 'text-ink-3'}`}>{(cssBytes / 1024).toFixed(1)} / 20 KB</p>
        </section>
      </div>

      <div className="min-w-0 xl:sticky xl:top-4 self-start">
        <VideoPlayerPreview quizId={quizId} refreshKey={previewKey} styleOverride={style} />
      </div>
    </div>
  );
}
